// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

/**
 * Centralized route permission map (Sprint 10.8.0 — F-C3).
 *
 * Maps sidebar sections and route path prefixes to the gating permission
 * required to view/access them. Both AdminLayout (sidebar filtering) and
 * App.tsx (route guards) consume this same map.
 *
 * Design rules:
 *  - Sidebar sections require the module's primary _READ permission.
 *  - Dashboard, Profile, and Help Center are always visible (no entry here).
 *  - The dev login has ALL permissions so RBAC changes do not affect dev testing.
 */
import { Permission } from '@vn-grc/shared';

// ---------------------------------------------------------------------------
// Sidebar section → gating permission
// ---------------------------------------------------------------------------

/**
 * Maps sidebar section labels (the i18n key path used in AdminLayout) to the
 * permission required to display that section. Sections not listed here are
 * always visible (Dashboard, Profile, Help Center).
 *
 * A section whose leaves are each gated by their own route (every module whose
 * pages render on grants of their own) declares NO section gate in `AdminLayout`:
 * it shows when any of its leaves does, each leaf on its route's grant
 * (`isLinkVisible`), so a role holding one page's grant is not hidden from the
 * section by a grant its page never asks. Its entry here stays as the record of
 * the module's primary read (the grant the route prefix uses for the pages with
 * none of their own) for the scripts and tests that read this table. The sections
 * that still gate are those with a leaf whose route has no gate of its own
 * (Organizations, Platform), plus the modules with a single grant throughout.
 */
export const SIDEBAR_PERMISSION_MAP: Record<string, string> = {
  // Label key used as object identity — must match the label expression in AdminLayout menuItems
  rights_requests: Permission.CASE_READ,
  data_mapping: Permission.DM_SYSTEM_READ,
  governance: Permission.GOV_VENDOR_READ,
  consent: Permission.CONSENT_POLICY_READ,
  consent_properties: Permission.CONSENT_PROPERTY_READ,
  assessments: Permission.DPIA_READ,
  classification: Permission.TAXONOMY_READ,
  monitoring: Permission.PROGRAM_READ,
  incidents: Permission.INCIDENT_READ,
  connectors: Permission.CONNECTOR_READ,
  vendor_risk: Permission.VENDOR_RISK_CONFIG_READ,
  deletion_orchestration: Permission.DELETION_ORCHESTRATION_READ,
  discovery: Permission.DISCOVERY_READ,
  incident_ops: Permission.INCIDENT_OPS_PLAYBOOK_LINK_READ,
  // The catch-all grant of the Program Governance routes (`program-governance` in
  // `ROUTE_PERMISSIONS`). The SECTION is not gated on it: it shows when any of its links does,
  // each link on its own route's grant (`AdminLayout`, `isLinkVisible`). Kept as the record of
  // that catch-all for the scripts that read this table.
  program_governance: Permission.MATURITY_MODEL_READ,
  access_accountability: Permission.ACCESS_LOG_READ,
  onboarding: Permission.ONBOARDING_WIZARD_READ,
  localization: Permission.MARKET_PACK_READ,
  partner_workspace: Permission.PARTNER_ORG_READ,
  task_management: Permission.TM_TASK_READ,
  // Sprint 44.3.0 B6 — Platform parent-section gate weakened from
  // PLATFORM_USERS_READ to PLATFORM_FILES_BROWSE so that case_manager
  // (seeded with platform:files:browse + detail:read + metrics:read at
  // seed-roles.ts:667-669) can reach Platform via nav and see the Files
  // sub-section. Each Platform child sub-section still carries its own
  // finer-grained requiredPermission (see AdminLayout.tsx:1348+).
  platform: Permission.PLATFORM_FILES_BROWSE,
  compliance_forms: Permission.FORMS_TEMPLATE_READ,
  org_groups: Permission.GROUP_READ,
  privacy_scanner: Permission.PRIVACY_SCANNER_READ,
  audit_trail: Permission.AUDIT_EVENTS_READ,
  data_subjects: Permission.DATA_SUBJECT_READ,
  // Sprint 47.5.2 — Forseti AI Legal Advisor (Projects module).
  // Sprint 63.22.0 (OD-A6) — this no longer gates the Forseti sidebar GROUP:
  // the group gated every leaf on `AI_PROJECTS_READ` while its Chats leaf opens
  // routes that require `AI_ADVISOR_CHAT` (below). Each leaf now carries its
  // route's permission and the group shows when any leaf does. The entry stays
  // as the Projects module's own gate, which is what it names.
  forseti: Permission.AI_PROJECTS_READ,
};

// ---------------------------------------------------------------------------
// Route prefix → gating permission
// ---------------------------------------------------------------------------

/**
 * Maps route path prefixes (relative to /admin/) to the permission required
 * for access. Checked by PermissionGate in App.tsx. Order matters — first
 * match wins, so more specific prefixes must come before broader ones.
 *
 * Routes not listed here are always accessible to authenticated users
 * (e.g. /admin, /admin/profile, /admin/help).
 */
export const ROUTE_PERMISSION_MAP: Array<{
  prefix: string;
  /**
   * `null` — authenticated only. Used where a narrower route must escape a
   * broader prefix's gate because its backend asks for nothing beyond a
   * session; it resolves to `undefined` like an unmapped route.
   */
  permission: string | null;
  /**
   * Further permissions that ALSO open the route — any one of `permission`
   * and these is enough. For a page that holds several registers, each behind
   * its own list permission, so that a role granted one register is not
   * refused the page (Sprint 63.23.0 gate E12.5 pass 1, P2-12). The page then
   * renders only the sections the viewer holds. `getRoutePermission` keeps
   * returning `permission`; anything deciding whether the route OPENS asks
   * `routeOpensFor`.
   */
  alsoAdmits?: readonly string[];
}> = [
  // Rights Requests & Templates
  //
  // The batch page runs on `POST /cases/batch-autocomplete`, which requires case
  // import AND Forseti chat. A role with case read alone used to open the page
  // and meet a 403 on every run. The sidebar leaf carries the chat half
  // (AdminLayout); this carries the import half. First match wins, so it sits
  // above the module prefix.
  { prefix: 'rights-requests/batch-autocomplete', permission: Permission.CASE_IMPORT },
  // The CMP outbound-sync settings page reads and writes `/rights-requests/cmp-sync/config`,
  // which asks the SLA configuration grant (rights-requests/cmp-dsar-routes.ts), and the page
  // gates on that grant. Ahead of the module prefix (first match wins).
  { prefix: 'rights-requests/settings/cmp-integration', permission: Permission.SLA_CONFIG_READ },
  { prefix: 'rights-requests', permission: Permission.CASE_READ },
  // Sprint 43.5.1 — `/admin/templates` removed; response templates now live
  // under the rights-requests prefix (/admin/rights-requests/response-templates).

  // Audit Trail — `/admin/audit/advisor-lineage` is an alias of
  // `/admin/forseti/audit-lineage` and shares the same gate (AI_ADVISOR_CHAT).
  { prefix: 'audit/advisor-lineage', permission: Permission.AI_ADVISOR_CHAT },
  { prefix: 'audit', permission: Permission.AUDIT_EVENTS_READ },

  // Sprint 62.2.5 — cross-framework evidence packs.
  //
  // Mapped deliberately. `PermissionGate` lets an UNMAPPED prefix through with
  // no gate at all, and the shipped `pdpl-evidence-packs` prefix is exactly
  // that — unmapped, and therefore ungated at the route layer. Repeating it for
  // a surface that assembles and dispatches regulator-facing artefacts would be
  // inheriting a hole rather than a pattern. The routes themselves are gated
  // server-side regardless; this is the client-side half.
  { prefix: 'framework-evidence-packs', permission: Permission.FRAMEWORK_EVIDENCE_PACK_READ },

  // Sprint 62.39.0 — the Cross-Border Identity Exchanges register. Mapped on
  // arrival, as 62.2.5 and 62.3.0 did, rather than joining the trust-evidence
  // leaves enumerated in the route-permission test's KNOWN_UNMAPPED backlog.
  // The full frontend suite caught it unmapped.
  { prefix: 'trust-evidence/identity-exchanges', permission: Permission.TRUST_EVIDENCE_READ },
  // The other Trust & Identity Evidence pages (identities, providers, credentials, submissions,
  // agreements) had NO door: an unmapped prefix passes `PermissionGate` with no client-side
  // gate, and the pages, their sidebar leaves and the API all ask `trust_evidence:read`.
  { prefix: 'trust-evidence', permission: Permission.TRUST_EVIDENCE_READ },

  // Regulatory Artifacts had no door either. The restricted-access register is custody-only
  // (its sidebar leaf and every API behind it ask the custody grant), ahead of the catalog,
  // the verification queue and the artifact detail, which ask the read grant.
  { prefix: 'regulatory-artifacts/grants', permission: Permission.REGULATORY_ARTIFACT_CUSTODY },
  { prefix: 'regulatory-artifacts', permission: Permission.REGULATORY_ARTIFACT_READ },

  // Sprint 62.3.0 — the AI system register. Mapped for the same reason: an
  // UNMAPPED prefix passes `PermissionGate` with no client-side gate at all,
  // and this surface lists which AI systems an organisation runs and what they
  // decide about people.
  { prefix: 'ai-governance', permission: Permission.AI_GOVERNANCE_READ },

  // Sprint 62.4.0 — regulatory classification. Same reasoning again: an
  // UNMAPPED prefix passes `PermissionGate` with no client-side gate at all,
  // and this surface reports which of an organisation's data assets and systems
  // fall inside a statutory list. `:read` is the floor; `:write` and `:review`
  // gate the controls inside each page, because a reader must be able to see a
  // classification without being able to make one.
  { prefix: 'regulatory-classification', permission: Permission.REGULATORY_CLASSIFICATION_READ },

  // Sprint 62.13.0 — penalty exposure and enforcement cases. The exposure
  // surfaces show what an organisation could be fined and where its evidence is
  // thin; a case is the record of a live authority action. Both are `:read` at
  // the route floor — the manage permission gates the editors and transitions
  // inside each page, because counsel must be able to read a case they cannot
  // move.
  { prefix: 'penalty-exposure', permission: Permission.PENALTY_EXPOSURE_READ },
  { prefix: 'enforcement-cases', permission: Permission.ENFORCEMENT_CASE_READ },
  // Sprint 62.14.0 — the route floor learned from 62.13.0's own defect: the
  // row is added in the same commit as the routes, not found by the census.
  { prefix: 'authority-directions', permission: Permission.AUTHORITY_DIRECTION_READ },
  { prefix: 'legal-holds', permission: Permission.LEGAL_HOLD_READ },
  { prefix: 'regulated-accounts', permission: Permission.REGULATED_ACCOUNT_READ },
  { prefix: 'product-classifications', permission: Permission.REGULATORY_CLASSIFICATION_READ },
  { prefix: 'conformity-certificates', permission: Permission.REGULATORY_CLASSIFICATION_READ },
  // Sprint 62.19.0 — sector licences, marketing approvals, investor
  // verifications, account-data disclosures, regulatory disclosures. Five
  // prefixes, each on its module's `:read`; the manage/approve tokens gate the
  // controls inside the pages, because a reader must be able to see a licence
  // or a refusal without being able to change or attempt one.
  // Sprint 62.55.0 (UC-VN-109 F-2, F-3) — the period view is the fee-line read's
  // own page, and the application pages also open on it (any-of), so the finance
  // function reaches the records it pays for without the licence register's read.
  // Both ahead of the `sector-licences` floor, so the longer prefix wins.
  { prefix: 'sector-licences/fee-lines', permission: Permission.REGULATORY_FEE_LINE_READ },
  {
    prefix: 'sector-licences/applications',
    permission: Permission.SECTOR_LICENCE_READ,
    alsoAdmits: [Permission.REGULATORY_FEE_LINE_READ],
  },
  { prefix: 'sector-licences', permission: Permission.SECTOR_LICENCE_READ },
  // Sprint 62.24.0 — the two registers the September banking / data-services
  // instruments needed; each gated on its own :read like every Sector Overlays leaf.
  { prefix: 'credit-information-supply', permission: Permission.CREDIT_INFORMATION_SUPPLY_READ },
  { prefix: 'data-service-providers', permission: Permission.DATA_SERVICE_PROVIDER_READ },
  // Sprint 62.50.0 — the five supervision-information registers, on the overlay's
  // read grant; each register's own write grant gates the controls inside its page.
  { prefix: 'pcf-supervision', permission: Permission.PCF_SUPERVISION_READ },
  // Sprint 62.51.0 — the seven data-exploitation registers, on the module's read
  // grant; `data_exploitation:manage` gates the record controls inside each page.
  { prefix: 'data-exploitation', permission: Permission.DATA_EXPLOITATION_READ },
  // Sprint 62.52.0 — the cybersecurity assurance index registers, on the module's read
  // grant; `cybersecurity_assurance_index:write` gates the record controls inside each
  // page, and each period's own read gate (membership or `:restricted_read`) is the server's.
  {
    prefix: 'cybersecurity-assurance-index',
    permission: Permission.CYBERSECURITY_ASSURANCE_INDEX_READ,
  },
  // Sprint 62.49.0 — the nine food traceability registers, on the module's read grant;
  // `food_traceability:write` / `:approve` / `:grant` gate the controls inside each page.
  { prefix: 'food-traceability', permission: Permission.FOOD_TRACEABILITY_READ },
  // Sprint 62.56.0 — the biometric capture registers, on the overlay's read grant;
  // each register's own write grant gates the controls inside its page.
  { prefix: 'biometric-capture', permission: Permission.BIOMETRIC_CAPTURE_READ },
  // Sprint 63.24.0 Slice A — controlled-testing cases, on the case module's :read.
  { prefix: 'controlled-testing', permission: Permission.CONTROLLED_TESTING_CASE_READ },
  { prefix: 'marketing-approvals', permission: Permission.MARKETING_APPROVAL_READ },
  { prefix: 'investor-verifications', permission: Permission.INVESTOR_VERIFICATION_READ },
  { prefix: 'account-data-disclosures', permission: Permission.ACCOUNT_DATA_DISCLOSURE_READ },
  { prefix: 'regulatory-disclosures', permission: Permission.REGULATORY_DISCLOSURE_READ },
  // Sprint 62.20.0 — the placeholder register, on the module's `:read`. The
  // `:write` token gates the intake form and the edit inside the pages: a
  // reader must be able to see what has been recorded, and the register's
  // whole contract is that seeing it changes nothing.
  { prefix: 'regulatory-placeholders', permission: Permission.REGULATORY_PLACEHOLDERS_READ },
  // Sprint 62.18.0 — the issued-record register, at the B0-fixed route. On the
  // module's `:read`; the manage/issue/share tokens gate the controls inside
  // the pages, because a reader must be able to see a version and a refusal
  // without being able to issue or share one.
  { prefix: 'student-records', permission: Permission.ISSUED_RECORD_READ },
  // Sprint 65.1.0 — the DPO work programme (list and one duty's detail), on the
  // module's OWN `:read`. The export token gates the export controls inside the
  // page, and `:manage` gates nothing here because this slice mounts no
  // editing surface. Mapped for the reason stated below: an UNMAPPED prefix
  // does not inherit the Governance section's gate, it falls out of
  // `PermissionGate` entirely.
  { prefix: 'compliance-operations', permission: Permission.COMPLIANCE_OBLIGATION_READ },
  // Sprint 62.17.0 — the publication register, the appointed-representative
  // register and the AI-assisted publication assessment. Each on its module's
  // `:read`; the manage / release / approve tokens gate the controls inside the
  // pages, because a reader must be able to see a deposit, a refused release
  // attempt and a superseded appointment without being able to make one.
  { prefix: 'publication-assessments', permission: Permission.PUBLICATION_ASSESSMENT_READ },
  { prefix: 'publications', permission: Permission.PUBLICATION_READ },
  { prefix: 'legal-representatives', permission: Permission.LEGAL_REPRESENTATIVE_READ },

  // Sprint 62.7.0 — public data exchange, payments/eKYC evidence and platform
  // evidence. Twelve routes across three prefixes, mapped for the reason stated
  // three entries up and now stated once more because it keeps being the thing
  // that is forgotten: an UNMAPPED prefix does not fall back to the owning
  // section's gate, it falls out of `PermissionGate` entirely and renders
  // `<Outlet />` with no client-side check at all.
  //
  // The sidebar leaves already carry their own `requiredPermission`, so the
  // *navigation* was gated; the *route* was not. A user who never sees a
  // Payments leaf could still type `/admin/payments-evidence/verifications` and
  // land on the page — the server denies the data and each page has a `!canRead`
  // branch, so this was never a data leak, but it is the one module in the
  // product where reaching the surface at all was unguarded.
  //
  // `:read` is the floor for all twelve. The narrower verbs these modules own
  // — release, unmask, reconcile, respond — gate acts, not arrival, and are
  // enforced at the control and at the API. A reader must be able to see a
  // published release without being able to publish one.
  //
  // Prefix form covers the detail routes (`.../datasets/:datasetId`,
  // `.../verifications/:verificationId`) that get no sidebar leaf of their own,
  // which is precisely where an entry-per-leaf map would have left a hole.
  { prefix: 'public-data', permission: Permission.PUBLIC_DATA_READ },
  // Sprint 62.46.0 — ahead of the broad `payments-evidence` entry (first match
  // wins): the case register and its detail route are restricted to
  // `aml_case:read`, the grant its sidebar leaf is gated on. A payments-evidence
  // reader without it is refused at the route, and a case reader without the
  // payments read still reaches the page its leaf offers.
  { prefix: 'payments-evidence/aml-cases', permission: Permission.AML_CASE_READ },
  { prefix: 'payments-evidence', permission: Permission.PAYMENTS_EVIDENCE_READ },
  // Ahead of the broad `platform` entry below. `getRoutePermission` is
  // first-match-wins on `=== prefix || startsWith(prefix + '/')`, so
  // `platform-evidence/sellers` would not in fact be caught by `platform` —
  // but the two prefixes are one hyphen apart and the next person to reorder
  // this array should not have to re-derive that.
  { prefix: 'platform-evidence', permission: Permission.PLATFORM_EVIDENCE_READ },

  // ── Sprint 62.8.0 — manufacturing, nine leaves and SIX permissions ──
  //
  // Nine entries rather than one `manufacturing` prefix, because the nine leaves
  // are gated on six different permissions: a quality lead, an EPR lead, an EHS
  // steward and a plant OT lead are four people in four reporting lines, and a
  // single prefix entry would have gated the chemical register on whoever keeps
  // batch records.
  //
  // **These were missing entirely, and `route-permissions.test.ts` said so in its
  // own failure message**: `PermissionGate` renders `<Outlet />` for an unmapped
  // prefix, so all nine were pages any authenticated user could reach by URL. No
  // data was exposed — every page renders `RestrictedState` on `!canRead` and the
  // API 403s — so this is the defence-in-depth layer rather than the only one, and
  // it is step 12 of the new-module checklist, skipped.
  { prefix: 'manufacturing/products', permission: Permission.MANUFACTURING_PRODUCT_READ },
  { prefix: 'manufacturing/batches', permission: Permission.MANUFACTURING_PRODUCT_READ },
  // The ledger is `:read`, not `:append` — reading the chain is what a recall, an
  // audit and a customer complaint all need; appending is the gated act.
  { prefix: 'manufacturing/trace', permission: Permission.MANUFACTURING_PRODUCT_READ },
  { prefix: 'manufacturing/conformity', permission: Permission.MANUFACTURING_CONFORMITY_READ },
  { prefix: 'manufacturing/capa-recall', permission: Permission.MANUFACTURING_RECALL_READ },
  { prefix: 'manufacturing/epr', permission: Permission.MANUFACTURING_EPR_READ },
  { prefix: 'manufacturing/chemicals', permission: Permission.MANUFACTURING_SUBSTANCE_READ },
  // Sprint 63.31.0 — the chemical traceability declarations view (E17: the substance grants).
  {
    prefix: 'manufacturing/chemical-traceability',
    permission: Permission.MANUFACTURING_SUBSTANCE_READ,
  },
  { prefix: 'manufacturing/ot-assets', permission: Permission.MANUFACTURING_OT_READ },
  { prefix: 'manufacturing/remote-access', permission: Permission.MANUFACTURING_OT_READ },

  // ── Sprint 62.9.0 — logistics, seven leaves and FIVE permissions ──
  //
  // Seven entries rather than one `logistics` prefix, because the seven leaves
  // are gated on five different permissions. A customs lead, a courier
  // operations lead, a claims handler and whoever holds the organisation's
  // operating authorisations are four people in four reporting lines, and a
  // single prefix entry would have gated the postal authorisation register on
  // whoever keeps shipment paperwork.
  //
  // `PermissionGate` renders `<Outlet />` for an unmapped prefix, so an omission
  // here makes a page reachable by URL to any authenticated user. No data would
  // be exposed — every page renders `RestrictedState` on `!canRead` and the API
  // 403s — so this is the defence-in-depth layer rather than the only one, and it
  // is step 12 of the new-module checklist, which 62.8.0 skipped.
  { prefix: 'logistics/shipments', permission: Permission.LOGISTICS_SHIPMENT_READ },
  { prefix: 'logistics/customs', permission: Permission.LOGISTICS_CUSTOMS_READ },
  // `:read`, not `:clearance:append` — what the authority did is evidence anyone
  // with the customs read grant needs; appending to the ledger is the gated act.
  { prefix: 'logistics/inspections', permission: Permission.LOGISTICS_CUSTOMS_READ },
  { prefix: 'logistics/parcels', permission: Permission.LOGISTICS_PARCEL_READ },
  // The parcel grant, not the delivery-proof grant: the timeline is the parcel
  // register's, and the proof panel on that page carries its own read permission
  // and renders nothing at all without it. Gating the whole route on the proof
  // permission would hide the timeline from the operator who records it.
  { prefix: 'logistics/delivery', permission: Permission.LOGISTICS_PARCEL_READ },
  { prefix: 'logistics/claims', permission: Permission.LOGISTICS_CLAIM_READ },
  { prefix: 'logistics/authorisations', permission: Permission.LOGISTICS_AUTHORISATION_READ },
  // Sprint 62.55.0 — the five customs tax-administration leaves, on the module's read grant.
  { prefix: 'logistics/tax-dossiers', permission: Permission.CUSTOMS_TAX_ADMINISTRATION_READ },
  { prefix: 'logistics/tax-payments', permission: Permission.CUSTOMS_TAX_ADMINISTRATION_READ },
  { prefix: 'logistics/duty-free-lists', permission: Permission.CUSTOMS_TAX_ADMINISTRATION_READ },
  { prefix: 'logistics/tax-examinations', permission: Permission.CUSTOMS_TAX_ADMINISTRATION_READ },
  { prefix: 'logistics/tax-transition', permission: Permission.CUSTOMS_TAX_ADMINISTRATION_READ },

  // Settings (top-level legacy path)
  // Each settings page that gates on a grant of its own is let in by that grant; the rest of
  // `settings` stays on the platform settings read.
  { prefix: 'settings/license', permission: Permission.LICENSE_READ },
  // Sprint 62.53.0 (AC9) — the Organization profile page reads the sectors and
  // the profile statements, every one of them gated on the platform security
  // read (`org-settings/routes.ts`), so the page is too.
  { prefix: 'settings/sectors', permission: Permission.PLATFORM_SECURITY_READ },
  {
    prefix: 'settings/assessment-triggers',
    permission: Permission.ASSESSMENT_TRIGGER_CONFIGURE,
  },
  // The legacy path of the export schedules page; the same read as `platform/export-schedules`.
  { prefix: 'settings/export-schedules', permission: Permission.PLATFORM_EXPORT_SCHEDULES_READ },
  { prefix: 'settings', permission: Permission.PLATFORM_SETTINGS_READ },

  // Platform: Users, Roles
  { prefix: 'users', permission: Permission.PLATFORM_USERS_READ },
  { prefix: 'roles', permission: Permission.PLATFORM_USERS_READ },

  // Platform: Security
  //
  // Break Glass is gated by what its API routes enforce, not by the security
  // read grant: listing and deciding need `:approve`, asking needs `:request`
  // (breakglass/routes.ts), and a requester is a different person from an
  // approver. Either one opens the page, which then shows only the half the
  // viewer holds. Ahead of the broad `security` entry (first match wins).
  {
    prefix: 'security/break-glass',
    permission: Permission.PLATFORM_BREAKGLASS_REQUEST,
    alsoAdmits: [Permission.PLATFORM_BREAKGLASS_APPROVE],
  },
  // MFA Settings is every signed-in user's own page: enrolling, confirming,
  // turning off and regenerating codes act on the caller's account and the self
  // routes (`mfa/routes.ts`) ask for a session only. Only the organisation
  // section on the page needs `platform:security:read` / `:write`, and the server
  // refuses that part. `null` = authenticated only, ahead of the broad entry.
  { prefix: 'security/mfa', permission: null },
  // Sprint 63.29.0 — Delegations reads `platform:delegations:read` (delegations/routes.ts), not the security grant.
  { prefix: 'security/delegations', permission: Permission.PLATFORM_DELEGATIONS_READ },
  { prefix: 'security', permission: Permission.PLATFORM_SECURITY_READ },

  // Platform: Access Reviews, Attestation, SoD
  //
  // Sprint 63.22.0 gate E12.5 pass 2 — My Reviews is the reviewer's own
  // queue. `GET /api/v1/access-reviews/my-reviews` requires authentication
  // only (access-review/routes.ts, no `requirePermission`) and filters on the
  // caller's user id; routed through the `access-reviews` prefix below it
  // required `PLATFORM_ACCESS_REVIEW_READ`, so an assigned reviewer without
  // campaign-read rights was refused their own reviews.
  //
  // Gate E12.5 pass 3 (P1) — pass 2 declared it `null` (authenticated only),
  // which put a Platform section with My Reviews in front of viewer,
  // contributor and support, whose only action on it (PATCH
  // /access-reviews/:id/items/:itemId) requires `PLATFORM_ACCESS_REVIEW_DECIDE`
  // (access-review/routes.ts) and answered 403. The page's purpose is
  // deciding, so it is gated on the decide grant — not on campaign read.
  { prefix: 'access-reviews/my-reviews', permission: Permission.PLATFORM_ACCESS_REVIEW_DECIDE },
  { prefix: 'access-reviews', permission: Permission.PLATFORM_ACCESS_REVIEW_READ },
  // The create routes call only the write endpoint, and their pages gate on the
  // write grant, so the door must too — ahead of the read entries below (first
  // match wins). The edit and history routes load the record first, which is a
  // read, so the read entry is the right door for them.
  { prefix: 'attestation-schedules/new', permission: Permission.PLATFORM_ATTESTATION_WRITE },
  { prefix: 'attestation-schedules', permission: Permission.PLATFORM_ATTESTATION_READ },
  { prefix: 'sod-rules/new', permission: Permission.PLATFORM_SOD_WRITE },
  { prefix: 'sod-rules', permission: Permission.PLATFORM_SOD_READ },

  // Data Mapping
  //
  // The classification exceptions queue is served by the classification routes
  // on `classification_exception:read` (classification/routes.ts), not by the
  // data-mapping system grant. Ahead of the broad entry (first match wins).
  { prefix: 'data-mapping/exceptions', permission: Permission.CLASSIFICATION_EXCEPTION_READ },
  //
  // Every other Data Mapping page renders on its own register's grant, so its door asks the
  // same grant (a role holding `dm:flow:read` alone opens Flows; a role holding only
  // `dm:system:read` no longer opens a page that answers "no permission").
  { prefix: 'data-mapping/activities', permission: Permission.DM_ACTIVITY_READ },
  { prefix: 'data-mapping/attestation', permission: Permission.DM_ATTESTATION_READ },
  { prefix: 'data-mapping/categories', permission: Permission.DM_CATEGORY_READ },
  { prefix: 'data-mapping/evidence', permission: Permission.DM_EVIDENCE_READ },
  { prefix: 'data-mapping/exports', permission: Permission.DM_ROPA_READ },
  { prefix: 'data-mapping/flows', permission: Permission.DM_FLOW_READ },
  { prefix: 'data-mapping/governance', permission: Permission.DM_OWNERSHIP_READ },
  // History lists `GET /classification/audit` (taxonomy read) and Inventory lists
  // `GET /classification/inventory` (classification review read): those are the grants the
  // pages render on, so the door asks them.
  { prefix: 'data-mapping/history', permission: Permission.TAXONOMY_READ },
  { prefix: 'data-mapping/inventory', permission: Permission.CLASSIFICATION_REVIEW_READ },
  { prefix: 'data-mapping/purposes', permission: Permission.DM_PURPOSE_READ },
  { prefix: 'data-mapping/registry', permission: Permission.DM_REGISTRY_READ },
  { prefix: 'data-mapping/retention', permission: Permission.DM_RETENTION_READ },
  { prefix: 'data-mapping/review', permission: Permission.DM_REVIEW_READ },
  { prefix: 'data-mapping/ropa-completeness', permission: Permission.DM_ACTIVITY_READ },
  { prefix: 'data-mapping', permission: Permission.DM_SYSTEM_READ },

  // Governance
  // Each Governance page renders on its own grant; the vendor register (`governance/vendors`)
  // and the unlisted routes stay on the vendor read.
  { prefix: 'governance/approvals', permission: Permission.GOV_APPROVAL_READ },
  { prefix: 'governance/audit-trail', permission: Permission.GOV_AUDIT_READ },
  { prefix: 'governance/board-report', permission: Permission.GOV_SCORE_READ },
  {
    prefix: 'governance/cross-border/filings',
    permission: Permission.GOV_CROSS_BORDER_FILING_READ,
  },
  { prefix: 'governance/cross-border', permission: Permission.GOV_CROSSBORDER_READ },
  { prefix: 'governance/drift', permission: Permission.GOV_DRIFT_READ },
  { prefix: 'governance/evidence', permission: Permission.GOV_EVIDENCE_READ },
  { prefix: 'governance/offboarding', permission: Permission.GOV_OFFBOARDING_READ },
  { prefix: 'governance/periodic-reviews', permission: Permission.GOV_PERIODIC_REVIEW_READ },
  { prefix: 'governance/playbooks', permission: Permission.GOV_PLAYBOOK_READ },
  { prefix: 'governance/scores', permission: Permission.GOV_SCORE_READ },
  { prefix: 'governance/subprocessors', permission: Permission.GOV_SUBPROCESSOR_READ },
  { prefix: 'governance', permission: Permission.GOV_VENDOR_READ },

  // Consent
  // Each Consent register page renders on its own grant; policies, purposes, audit, change
  // awareness, gap analysis and the evidence/import pages stay on the policy read.
  // Owner ruling 2026-10-21 (OR-6, fix-sprint-20261017-1500 F7 item 3) — each
  // CMP integration page opens on the grant its reads need, so a holder of the
  // door is never shown a page whose every read answers 403. The webhook
  // configuration and the purpose mappings are read under
  // `consent_cmp_snapshot:write` (`cmp-consent-routes.ts`); the overview reads
  // the configuration or the web-consent analytics (`consent:policy:read`).
  { prefix: 'consent/cmp-integration/webhook', permission: Permission.CONSENT_CMP_SNAPSHOT_WRITE },
  {
    prefix: 'consent/cmp-integration/purpose-mapping',
    permission: Permission.CONSENT_CMP_SNAPSHOT_WRITE,
  },
  { prefix: 'consent/cmp-integration/remap', permission: Permission.CONSENT_CMP_SNAPSHOT_WRITE },
  {
    prefix: 'consent/cmp-integration',
    permission: Permission.CONSENT_CMP_SNAPSHOT_WRITE,
    alsoAdmits: [Permission.CONSENT_POLICY_READ],
  },
  { prefix: 'consent/reconciliation', permission: Permission.CONSENT_RECONCILIATION_READ },
  { prefix: 'consent/snapshots', permission: Permission.CONSENT_CMP_SNAPSHOT_READ },
  { prefix: 'consent/sync', permission: Permission.CONSENT_SYNC_READ },
  { prefix: 'consent/withdrawal-propagation', permission: Permission.CONSENT_PROPAGATION_READ },
  // fix-sprint-20260915-0141 F16 — the evidence register's six routes all
  // require the workflow read (consent/routes.ts `/evidence*`), so its door is
  // that grant. It inherited `consent`'s policy read, which under a custom role
  // showed the leaf to a policy-only holder whose every call 403'd and hid it
  // from a workflow-only holder the API would answer.
  { prefix: 'consent/evidence', permission: Permission.CONSENT_WORKFLOW_READ },
  // The workflow import page gates on the import grant, not a read grant; its door stays on
  // the policy read it was opened by (ahead of the workflows entry below).
  { prefix: 'consent/workflows/import', permission: Permission.CONSENT_POLICY_READ },
  { prefix: 'consent/workflows', permission: Permission.CONSENT_WORKFLOW_READ },
  { prefix: 'consent', permission: Permission.CONSENT_POLICY_READ },

  // Sprint 62.28.0 — marketing-communications registers. Their own prefix
  // rather than a leaf under `consent`, so the route guard checks the grant
  // the routes themselves check, not the consent-policy grant.
  //
  // Sprint 62.55.0 (UC-VN-109 F-2) — the sender-name certificates page also opens
  // on the cross-register fee-line read (any-of), so the finance function records
  // charge lines without the consent and suppression registers' read. Ahead of
  // the `marketing-communications` floor, so the longer prefix wins.
  {
    prefix: 'marketing-communications/sender-name-certificates',
    permission: Permission.MARKETING_COMMUNICATIONS_READ,
    alsoAdmits: [Permission.REGULATORY_FEE_LINE_READ],
  },
  {
    prefix: 'marketing-communications',
    permission: Permission.MARKETING_COMMUNICATIONS_READ,
  },

  // Consent Properties
  {
    prefix: 'consent-properties/analytics',
    permission: Permission.CONSENT_PROPERTY_ANALYTICS_READ,
  },
  { prefix: 'consent-properties/bindings', permission: Permission.CONSENT_BINDING_READ },
  {
    prefix: 'consent-properties/change-requests',
    permission: Permission.CONSENT_CHANGE_REQUEST_READ,
  },
  { prefix: 'consent-properties/rollouts', permission: Permission.CONSENT_GOLDEN_TEMPLATE_READ },
  { prefix: 'consent-properties', permission: Permission.CONSENT_PROPERTY_READ },

  // Assessments
  // Transfers is its own register: the page, the Transfers sidebar leaf and every transfer
  // route in the API ask `transfer_assessment:read`, not the DPIA read the section used.
  { prefix: 'assessments/transfers', permission: Permission.TRANSFER_ASSESSMENT_READ },
  { prefix: 'assessments', permission: Permission.DPIA_READ },

  // Classification
  // Each page opens on the grant its own list route asks (classification/routes.ts), so a role
  // holding one register's read is not sent to a page whose data answers 403. Labeling and
  // Exports ask `classification:read`, the module prefix below.
  {
    prefix: 'classification/discovery-cycles',
    permission: Permission.CLASSIFICATION_DISCOVERY_READ,
  },
  { prefix: 'classification/enterprise-map', permission: Permission.CLASSIFICATION_REVIEW_READ },
  { prefix: 'classification/exceptions', permission: Permission.CLASSIFICATION_EXCEPTION_READ },
  { prefix: 'classification/gaps', permission: Permission.GAP_REPORT_READ },
  { prefix: 'classification/lifecycle', permission: Permission.LIFECYCLE_READ },
  { prefix: 'classification/review-tasks', permission: Permission.CLASSIFICATION_REVIEW_READ },
  { prefix: 'classification/taxonomies', permission: Permission.TAXONOMY_READ },
  { prefix: 'classification', permission: Permission.CLASSIFICATION_READ },

  // Monitoring
  // Programs and Periods stay on the program read; every other Monitoring page renders on its
  // own register's grant.
  {
    prefix: 'monitoring/authority-readiness',
    permission: Permission.MONITORING_AUTHORITY_READINESS_READ,
  },
  { prefix: 'monitoring/change-review', permission: Permission.MONITORING_CHANGE_REVIEW_READ },
  { prefix: 'monitoring/framework-link', permission: Permission.MONITORING_FRAMEWORK_LINK_READ },
  { prefix: 'monitoring/kpi', permission: Permission.KPI_READ },
  // The RACI list is `GET /program-governance/raci-assignments`, which asks the maturity read
  // (only the matrix export accepts the RACI read), so the page and its door ask the maturity read.
  { prefix: 'monitoring/raci', permission: Permission.MATURITY_READ },
  { prefix: 'monitoring/ric', permission: Permission.RIC_READ },
  { prefix: 'monitoring/task-templates', permission: Permission.TASK_TEMPLATE_READ },
  { prefix: 'monitoring/tasks', permission: Permission.REVIEW_TASK_READ },
  { prefix: 'monitoring', permission: Permission.PROGRAM_READ },

  // Incidents
  // The task list is served on the incident-task grant (incidents/routes.ts), not the
  // incident grant. Ahead of the broad entry (first match wins).
  { prefix: 'incidents/tasks', permission: Permission.INCIDENT_TASK_READ },
  {
    prefix: 'incidents/breach-notifications',
    permission: Permission.INCIDENT_BREACH_NOTIFICATION_READ,
  },
  { prefix: 'incidents', permission: Permission.INCIDENT_READ },

  // Connectors
  { prefix: 'connectors/credentials', permission: Permission.CREDENTIAL_READ },
  { prefix: 'connectors/event-bus', permission: Permission.EVENT_BUS_READ },
  { prefix: 'connectors', permission: Permission.CONNECTOR_READ },

  // Vendor Risk
  // Every vendor-risk page renders on its own register's grant. The questionnaire template
  // editor and preview carry no page gate of their own, so they stay on the config read
  // they were opened by (ahead of the questionnaires entry below).
  {
    prefix: 'vendor-risk/questionnaires/templates',
    permission: Permission.VENDOR_RISK_CONFIG_READ,
  },
  { prefix: 'vendor-risk/acceptances', permission: Permission.VENDOR_RISK_ACCEPTANCE_READ },
  { prefix: 'vendor-risk/dashboard', permission: Permission.VENDOR_RISK_SCORE_READ },
  { prefix: 'vendor-risk/gates', permission: Permission.VENDOR_GATE_READ },
  { prefix: 'vendor-risk/onboarding-cases', permission: Permission.VENDOR_ONBOARDING_CASE_READ },
  { prefix: 'vendor-risk/portfolio', permission: Permission.VENDOR_RISK_PORTFOLIO_READ },
  { prefix: 'vendor-risk/questionnaires', permission: Permission.VENDOR_QUESTIONNAIRE_READ },
  { prefix: 'vendor-risk/reassessments', permission: Permission.VENDOR_RISK_REASSESSMENT_READ },
  { prefix: 'vendor-risk/review-routing', permission: Permission.VENDOR_RISK_REVIEW_ROUTING_READ },
  { prefix: 'vendor-risk/reviews', permission: Permission.VENDOR_REVIEW_READ },
  { prefix: 'vendor-risk/scoring', permission: Permission.VENDOR_RISK_SCORE_READ },
  { prefix: 'vendor-risk/signals', permission: Permission.VENDOR_RISK_SIGNAL_READ },
  {
    prefix: 'vendor-risk/sub-processor-register',
    permission: Permission.VENDOR_ONBOARDING_CASE_READ,
  },
  { prefix: 'vendor-risk', permission: Permission.VENDOR_RISK_CONFIG_READ },

  // Sprint 47.5.8.1 — Contracts (first-class module).
  { prefix: 'contracts', permission: Permission.VENDOR_CONTRACT_READ },

  // Deletion Orchestration
  { prefix: 'deletion-orchestration/proof-chain', permission: Permission.DELETION_PROOF_READ },
  { prefix: 'deletion-orchestration/templates', permission: Permission.DELETION_TEMPLATE_READ },
  {
    prefix: 'deletion-orchestration/vendor-requests',
    permission: Permission.DELETION_VENDOR_REQUEST_READ,
  },
  {
    prefix: 'deletion-orchestration/vendor-verification',
    permission: Permission.DELETION_VENDOR_REQUEST_READ,
  },
  { prefix: 'deletion-orchestration', permission: Permission.DELETION_ORCHESTRATION_READ },

  // Discovery
  // Sprint 63.29.0 (#572) — Classification Rules lists `GET /discovery/classification-rules`,
  // served on `classification_rule:read`; ahead of the module prefix so the door is the grant
  // the page and its API ask.
  { prefix: 'discovery/classification-rules', permission: Permission.CLASSIFICATION_RULE_READ },
  { prefix: 'discovery', permission: Permission.DISCOVERY_READ },

  // Incident Ops
  // The Escalation Log is served on the regulatory-SLA grant
  // (incident-ops/routes.ts), not the playbook-link grant. Ahead of the broad
  // entry (first match wins).
  { prefix: 'incident-ops/escalations', permission: Permission.INCIDENT_OPS_REGULATORY_SLA_READ },
  // The remaining Incident Ops pages render on the grant of the register they read.
  {
    prefix: 'incident-ops/coordination-dashboard',
    permission: Permission.INCIDENT_OPS_COORDINATION_READ,
  },
  {
    prefix: 'incident-ops/evidence-chain',
    permission: Permission.INCIDENT_OPS_EVIDENCE_CHAIN_READ,
  },
  { prefix: 'incident-ops/metrics', permission: Permission.INCIDENT_OPS_REGULATORY_SLA_READ },
  { prefix: 'incident-ops/post-review', permission: Permission.INCIDENT_OPS_POST_REVIEW_READ },
  {
    prefix: 'incident-ops/regulator-exports',
    permission: Permission.INCIDENT_OPS_COORDINATION_READ,
  },
  {
    prefix: 'incident-ops/regulatory-sla',
    permission: Permission.INCIDENT_OPS_REGULATORY_SLA_READ,
  },
  { prefix: 'incident-ops/sla-dashboard', permission: Permission.INCIDENT_OPS_REGULATORY_SLA_READ },
  {
    prefix: 'incident-ops/sla-definitions',
    permission: Permission.INCIDENT_OPS_REGULATORY_SLA_READ,
  },
  {
    prefix: 'incident-ops/vendor-coordination',
    permission: Permission.INCIDENT_OPS_COORDINATION_READ,
  },
  { prefix: 'incident-ops', permission: Permission.INCIDENT_OPS_PLAYBOOK_LINK_READ },

  // Program Governance
  //
  // Each page is served on the grant its own API routes ask for (program-governance/
  // routes.ts), not on the maturity-model grant the section as a whole used to share.
  // The page gate was aligned to the API and the door left on the coarse prefix, so a
  // holder of only `executive_report:read` (or `trend_dashboard:read`, `change_log:read`,
  // ...) was admitted by the page and refused at the door. Ahead of the broad entry
  // (first match wins); Program Health, Maturity Models and the Governance Model stay on
  // the maturity-model grant their pages and APIs ask for.
  { prefix: 'program-governance/reporting-periods', permission: Permission.EXECUTIVE_REPORT_READ },
  { prefix: 'program-governance/summaries', permission: Permission.EXECUTIVE_REPORT_READ },
  { prefix: 'program-governance/trends', permission: Permission.TREND_DASHBOARD_READ },
  { prefix: 'program-governance/assessments', permission: Permission.MATURITY_ASSESSMENT_READ },
  {
    prefix: 'program-governance/handbook-assessments',
    permission: Permission.MATURITY_ASSESSMENT_READ,
  },
  { prefix: 'program-governance/readiness', permission: Permission.AUDIT_READINESS_READ },
  { prefix: 'program-governance/evidence-gaps', permission: Permission.AUDIT_READINESS_READ },
  { prefix: 'program-governance/audit-engagements', permission: Permission.AUDIT_READINESS_READ },
  { prefix: 'program-governance/remediation-plans', permission: Permission.AUDIT_READINESS_READ },
  {
    prefix: 'program-governance/obligation-mappings',
    permission: Permission.OBLIGATION_MAPPING_READ,
  },
  { prefix: 'program-governance/governance-documents', permission: Permission.GOV_DOC_READ },
  { prefix: 'program-governance/document-drafting', permission: Permission.GOV_DOC_READ },
  { prefix: 'program-governance/document-issuance', permission: Permission.GOV_DOC_READ },
  {
    prefix: 'program-governance/acknowledgment-tracking',
    permission: Permission.ACKNOWLEDGMENT_READ,
  },
  { prefix: 'program-governance/change-log', permission: Permission.CHANGE_LOG_READ },
  { prefix: 'program-governance/review-cadence', permission: Permission.REVIEW_CADENCE_READ },
  { prefix: 'program-governance', permission: Permission.MATURITY_MODEL_READ },

  /*
   * Sprint 65.6.0 — DT-01 training.
   *
   * Gated in `ROUTE_MODULE_MAP` on its SIDEBAR section's module
   * (`assessments`, fix sprint 20260927-0100 F7), never on a `training` id:
   * `training` is not a configurable module id. (The note here used to say
   * such an entry "404s for everyone"; `isModuleEnabled` answers `true` for an
   * id the config does not carry, so it would have gated nothing.)
   */
  { prefix: 'training', permission: Permission.TRAINING_READ },

  /*
   * Sprints 65.17.0 and 65.18.0 — ANON-01 runs, IRP-01 exercises.
   *
   * Neither is a configurable module id; both are gated in `ROUTE_MODULE_MAP`
   * on their sidebar section's module (`assessments`, F7), as the training
   * entry above is.
   *
   * Without these rows `PermissionGate` renders `<Outlet />` for an unmapped
   * prefix, so both registers were pages anyone authenticated could reach by
   * URL — the backend refused the reads, but the screen rendered.
   */
  { prefix: 'deidentification', permission: Permission.DEIDENTIFICATION_READ },
  { prefix: 'continuity', permission: Permission.CONTINUITY_READ },
  { prefix: 'legal-matters', permission: Permission.LEGAL_MATTERS_READ },
  // Sprint 65.21.0 — the gate for the credit-information register. The nav
  // leaf carries no permission field of its own; this row is the gate, and a
  // second one on the leaf would be a second place to forget.
  { prefix: 'credit-information', permission: Permission.CREDIT_INFORMATION_READ },

  /*
   * Sprint 65.6.0 (AC8) — DPO-01 and PUB-01.
   *
   * Two entries, not one on the shared `privacy-office` prefix. The backend
   * router carries two permission pairs on purpose (`dpo_appointment:*` and
   * `privacy_notice:*`, "separate registers with separate readers"), so a
   * single prefix entry would have to name one of them and would then refuse
   * the other register to a principal who legitimately holds only its token.
   *
   * Both are more specific than any broader prefix in this list and neither is
   * a parent of the other, so their relative order does not matter — but they
   * are listed before nothing that would shadow them. `privacy-office` is not a
   * configurable module id; the prefix is gated in `ROUTE_MODULE_MAP` on its
   * sidebar section's module (`assessments`, F7).
   */
  { prefix: 'privacy-office/appointments', permission: Permission.DPO_APPOINTMENT_READ },
  { prefix: 'privacy-office/notices', permission: Permission.PRIVACY_NOTICE_READ },

  // Access Accountability
  // The access log and evidence packages stay on the log read; every other Access
  // Accountability page renders on the grant of the register it shows. Pattern analysis
  // reads `/investigations/pattern/:userId` (ACCESS_INVESTIGATION_READ), so it is gated on that
  // grant. The all-time user summary (`/access-log/user/:userId/summary`) is read only when the
  // log read is held too; without it the page shows the analysis and leaves the summary out.
  { prefix: 'access-accountability/access-policies', permission: Permission.ACCESS_CONFIG_READ },
  { prefix: 'access-accountability/anchoring', permission: Permission.ACCESS_ANCHORING_VERIFY },
  { prefix: 'access-accountability/config', permission: Permission.ACCESS_CONFIG_READ },
  {
    prefix: 'access-accountability/investigations',
    permission: Permission.ACCESS_INVESTIGATION_READ,
  },
  {
    prefix: 'access-accountability/justifications',
    permission: Permission.ACCESS_JUSTIFICATION_READ,
  },
  {
    prefix: 'access-accountability/patterns',
    permission: Permission.ACCESS_INVESTIGATION_READ,
  },
  { prefix: 'access-accountability/review-campaigns', permission: Permission.ACCESS_CONFIG_READ },
  { prefix: 'access-accountability/sessions', permission: Permission.ACCESS_SESSION_READ },
  { prefix: 'access-accountability', permission: Permission.ACCESS_LOG_READ },

  // Help FAQ Editor (more specific than onboarding, must come first)
  { prefix: 'onboarding/help/faq-editor', permission: Permission.HELP_FAQ_EDIT },

  // Sprint 63.11.0 (#34) — the persona and use-case detail pages carry the
  // SAME permission as the `onboarding/personas` list they sit beside
  // (`ONBOARDING_WIZARD_READ`, which `GET /onboarding/maturity/persona-availability`
  // also requires). Declared explicitly so the two routes are named here, not
  // only reached through the broader prefix below.
  { prefix: 'onboarding/personas', permission: Permission.ONBOARDING_WIZARD_READ },
  { prefix: 'onboarding/use-cases', permission: Permission.ONBOARDING_WIZARD_READ },

  // Onboarding
  { prefix: 'onboarding/tours', permission: Permission.ONBOARDING_TOUR_READ },
  { prefix: 'onboarding', permission: Permission.ONBOARDING_WIZARD_READ },

  // Localization
  // The framework channel and link pages sit under a framework identifier, so the prefix names
  // the identifier as `:frameworkId`. The framework list and detail stay on the pack read.
  {
    prefix: 'localization/frameworks/:frameworkId/channels',
    permission: Permission.REGULATORY_FRAMEWORK_READ,
  },
  {
    prefix: 'localization/frameworks/:frameworkId/links',
    permission: Permission.REGULATORY_FRAMEWORK_READ,
  },
  { prefix: 'localization/language-packs', permission: Permission.LANGUAGE_PACK_READ },
  { prefix: 'localization/mappings', permission: Permission.REGULATORY_FRAMEWORK_READ },
  { prefix: 'localization/regulatory-updates', permission: Permission.REGULATORY_FRAMEWORK_READ },
  { prefix: 'localization/translations', permission: Permission.LANGUAGE_PACK_READ },
  { prefix: 'localization', permission: Permission.MARKET_PACK_READ },

  // Sprint 63.43.0 (Module 30, AC4) — the Sector Overlays Overview page opens on
  // the same grant as the Overlay Catalog beside it in Sector Overlays › Overlay
  // Management: `localization/sector-overlays` has no row of its own and resolves
  // through the `localization` row above to `MARKET_PACK_READ`. The two leaves of
  // the group therefore show and open together. The page reads only the navigation
  // projection (authenticated, Enterprise guard) and the registry.
  { prefix: 'sector-overlays', permission: Permission.MARKET_PACK_READ },

  // Partner Marketplace
  { prefix: 'partner-marketplace/audit', permission: Permission.PARTNER_AUDIT_READ },
  { prefix: 'partner-marketplace/brand-profiles', permission: Permission.PARTNER_BRAND_READ },
  { prefix: 'partner-marketplace/escalation', permission: Permission.PARTNER_TICKET_READ },
  { prefix: 'partner-marketplace/reports', permission: Permission.PARTNER_REPORT_READ },
  { prefix: 'partner-marketplace/service-tiers', permission: Permission.SERVICE_TIER_READ },
  { prefix: 'partner-marketplace/support-tickets', permission: Permission.PARTNER_TICKET_READ },
  {
    prefix: 'partner-marketplace/workflow-templates',
    permission: Permission.PARTNER_WORKFLOW_READ,
  },
  { prefix: 'partner-marketplace/workflows', permission: Permission.PARTNER_WORKFLOW_READ },
  { prefix: 'partner-marketplace', permission: Permission.PARTNER_ORG_READ },

  // Task Management
  // Calendar and the workflow pages render on their own grants.
  { prefix: 'tasks/calendar', permission: Permission.TM_CALENDAR_READ },
  { prefix: 'tasks/workflows/templates', permission: Permission.WF_TEMPLATE_READ },
  { prefix: 'tasks/workflows', permission: Permission.WF_DEFINITION_READ },
  { prefix: 'tasks', permission: Permission.TM_TASK_READ },

  // Sprint 63.22.0 gate E12.5 pass 2 — a user's own notification preferences.
  // Every preference endpoint (notifications/notification-routes.ts) requires
  // `PLATFORM_NOTIFICATIONS_PREFERENCES`; under the `platform` prefix below
  // the page required `PLATFORM_SETTINGS_READ`, which the endpoints never ask.
  {
    prefix: 'platform/notification-preferences',
    permission: Permission.PLATFORM_NOTIFICATIONS_PREFERENCES,
  },

  // The platform pages that gate on a grant of their own (the same grant their sidebar leaf
  // asks) are let in by it; the rest of `platform` stays on the settings read.
  { prefix: 'platform/files/browser', permission: Permission.PLATFORM_FILES_BROWSE },
  { prefix: 'platform/files/config', permission: Permission.PLATFORM_FILES_CONFIG_READ },
  { prefix: 'platform/files/dashboard', permission: Permission.PLATFORM_FILES_METRICS_READ },
  { prefix: 'platform/immutable-audit-trails', permission: Permission.IMMUTABLE_AUDIT_TRAILS_READ },
  { prefix: 'platform/modules', permission: Permission.PLATFORM_MODULE_CONFIG_READ },

  // Gate E12.5 pass 1 (F-P2-3) — the export schedules page and its run list read
  // `GET /export-schedules` and `GET /export-schedules/:id/runs`, both on the
  // export-schedule read; under the `platform` catch-all it asked the settings read,
  // which those routes never check.
  { prefix: 'platform/export-schedules', permission: Permission.PLATFORM_EXPORT_SCHEDULES_READ },

  // Platform (module config, settings, webhooks, etc.)
  { prefix: 'platform', permission: Permission.PLATFORM_SETTINGS_READ },

  // Compliance Forms
  // The forms pages render on `forms:instance:read`. The catalog and the template browser list
  // templates, which the API serves on `forms:template:read`; both pages render on that grant
  // too, so the door and the page agree.
  { prefix: 'forms/catalog', permission: Permission.FORMS_TEMPLATE_READ },
  { prefix: 'forms/templates', permission: Permission.FORMS_TEMPLATE_READ },
  { prefix: 'forms', permission: Permission.FORMS_INSTANCE_READ },

  // Org Groups
  // The group audit and shared-data pages sit under a group identifier (`:groupId`) and render
  // on grants of their own.
  { prefix: 'org-groups/:groupId/audit', permission: Permission.GROUP_AUDIT_READ },
  { prefix: 'org-groups/:groupId/merged', permission: Permission.GROUP_SHARED_DATA_READ },
  { prefix: 'org-groups/:groupId/shared', permission: Permission.GROUP_SHARED_DATA_READ },
  { prefix: 'org-groups', permission: Permission.GROUP_READ },

  // Privacy Scanner
  { prefix: 'privacy-scanner', permission: Permission.PRIVACY_SCANNER_READ },

  // Data Subjects
  { prefix: 'data-subjects', permission: Permission.DATA_SUBJECT_READ },

  // Sprint 47.5.2 — Forseti AI Legal Advisor.
  // `/admin/forseti/usage` is gated by AI_USAGE_VIEW; the projects pages
  // by AI_PROJECTS_READ. Order matters — narrower prefix first.
  { prefix: 'forseti/usage', permission: Permission.AI_PROVIDER_VIEW_USAGE },
  // Owner ruling 2026-10-10 — Forseti → AI Model (provider, key, model, effort).
  { prefix: 'forseti/ai-model', permission: Permission.AI_PROVIDER_CONFIGURE },
  { prefix: 'forseti/document-review', permission: Permission.AI_PROJECTS_READ },
  { prefix: 'forseti/projects', permission: Permission.AI_PROJECTS_READ },
  { prefix: 'forseti/workflows', permission: Permission.AI_WORKFLOWS_READ },
  { prefix: 'forseti/tabular-reviews', permission: Permission.AI_TABULAR_REVIEWS_READ },
  { prefix: 'forseti/audit-lineage', permission: Permission.AI_ADVISOR_CHAT },
  // Sprint 63.22.0 gate E12.5 pass 1 (P1) — these three pages each gate on
  // their own permission (`McpKeysPage`, `McpPendingWritesPage`,
  // `MemoryBrowsePage`) and their sidebar leaves always did, but the routes fell
  // through to `forseti` below (`AI_ADVISOR_CHAT`): an MCP administrator who
  // could not chat saw the leaf and landed on Access Denied.
  {
    prefix: 'forseti/mcp-keys',
    permission: Permission.AI_PROVIDER_MCP_LIST,
    alsoAdmits: [
      Permission.AI_PROVIDER_MCP_OAUTH_CLIENT_LIST,
      Permission.AI_PROVIDER_MCP_GRANT_LIST,
    ],
  },
  { prefix: 'forseti/mcp-pending', permission: Permission.AI_PROVIDER_MCP_PENDING_LIST },
  { prefix: 'forseti/memory', permission: Permission.AI_ADVISOR_MEMORY_READ },
  // Sprint 47.5.4a — Forseti chat shell.
  { prefix: 'forseti/chats', permission: Permission.AI_ADVISOR_CHAT },
  { prefix: 'forseti', permission: Permission.AI_ADVISOR_CHAT },
  { prefix: 'ai-advisor', permission: Permission.AI_ADVISOR_CHAT },
];

/**
 * Whether `prefix` covers `relativePath`: the path is the prefix or sits under it.
 * A prefix segment written `:name` stands for any one segment, so a route whose
 * identifier comes BEFORE its page (`org-groups/:groupId/audit`) can be named by
 * the route pattern `App.tsx` declares. A prefix without a `:` segment is the
 * plain string comparison the map always used.
 */
function prefixCovers(prefix: string, relativePath: string): boolean {
  if (!prefix.includes(':')) {
    return relativePath === prefix || relativePath.startsWith(prefix + '/');
  }
  const wanted = prefix.split('/');
  const actual = relativePath.split('/');
  if (actual.length < wanted.length) return false;
  return wanted.every((segment, i) =>
    segment.startsWith(':') ? actual[i] !== '' : segment === actual[i],
  );
}

/**
 * Look up the gating permission for a given route path (relative to /admin/).
 * Returns undefined if the route is always accessible.
 */
export function getRoutePermission(relativePath: string): string | undefined {
  for (const entry of ROUTE_PERMISSION_MAP) {
    if (prefixCovers(entry.prefix, relativePath)) {
      return entry.permission ?? undefined;
    }
  }
  return undefined;
}

/**
 * Every permission that opens the route — `permission` and its `alsoAdmits`
 * — or `undefined` when the route needs only a session.
 */
export function getRouteAdmittingPermissions(relativePath: string): readonly string[] | undefined {
  for (const entry of ROUTE_PERMISSION_MAP) {
    if (prefixCovers(entry.prefix, relativePath)) {
      return entry.permission ? [entry.permission, ...(entry.alsoAdmits ?? [])] : undefined;
    }
  }
  return undefined;
}

/**
 * Whether a viewer holding `hasPermission` may open the route: it needs no
 * permission, or the viewer holds ANY permission that admits it. The one
 * question `PermissionGate` (App.tsx) and `isLinkVisible` both ask, so a link
 * is shown exactly when its route opens.
 */
export function routeOpensFor(
  relativePath: string,
  hasPermission: (permission: string) => boolean,
): boolean {
  const admits = getRouteAdmittingPermissions(relativePath);
  return !admits || admits.some((p) => hasPermission(p));
}

/**
 * Sprint 63.22.0 gate E12.5 pass 1 (P1) — whether a sidebar link's
 * DESTINATION opens: the `PermissionGate` in `App.tsx` asks the same
 * `routeOpensFor` over the same `/admin`-relative path, and renders
 * `AccessDeniedPage` when it does not.
 */
function linkRouteOpensFor(to: string, hasPermission: (permission: string) => boolean): boolean {
  const relativePath = to.replace(/[?#].*$/, '').replace(/^\/admin\/?/, '');
  if (!relativePath) return true;
  return routeOpensFor(relativePath, hasPermission);
}

/**
 * A link is shown only when the user holds BOTH its own gate and the gate of
 * the route it opens. OD-A6 gave each Forseti leaf its own gate and then gated
 * Document Review on `AI_ADVISOR_CHAT` while its route requires
 * `AI_PROJECTS_READ`, so a chat-only role saw a link that landed on Access
 * Denied — and three leaves (MCP Keys, MCP Pending, Memory) had the inverse
 * shape. Checking a leaf's declared gate alone trusts every declaration to
 * agree with the route table; checking both makes the disagreement harmless
 * and `layouts/__tests__/sidebar-leaf-route-gate-census-63.22.0.test.tsx` names any leaf that
 * would still be shown over a route its viewer cannot open.
 */
export function isLinkVisible(
  link: { to?: string; requiredPermission?: string },
  hasPermission: (permission: string) => boolean,
): boolean {
  if (link.requiredPermission && !hasPermission(link.requiredPermission)) return false;
  if (!link.to) return true;
  return linkRouteOpensFor(link.to, hasPermission);
}

// ---------------------------------------------------------------------------
// Route prefix → ModuleId (for module-config visibility gating)
// ---------------------------------------------------------------------------

/**
 * Maps route path prefixes to the ModuleId that must be enabled for the route
 * to be accessible. Routes not listed here are always accessible regardless of
 * module configuration (e.g. dashboard, profile, help, platform settings).
 *
 * Sprint 15.6.0 — Module configuration sidebar + route gating.
 */
export const ROUTE_MODULE_MAP: Array<{ prefix: string; moduleId: string }> = [
  // Sprint 63.43.0 (Module 30, owner ruling OR-26; plan §3.3) — Sector Overlays
  // owns the Overview, the Overlay Catalog and the 56 overlay-exclusive (Class A)
  // views of `overlay-register-surfaces.ts`. These rows come FIRST because
  // `getRouteModule` returns the first match: each must precede the broader prefix
  // it would otherwise fall under — `localization/sector-overlays` before
  // `localization`, the five manufacturing rows before `manufacturing`, the six
  // logistics rows before `logistics`, the two licence-fee rows before
  // `sector-licences`, the sender-name certificates before
  // `marketing-communications`. Every other route under those prefixes keeps its
  // module (Class B, C and D views and the independent leaves: products,
  // conformity, capa-recall, ot-assets, remote-access, shipments, inspections,
  // tax-dossiers, tax-payments, parcels, delivery, the licence register itself,
  // the marketing consent registers). URLs do not change.
  // `config/__tests__/overlay-register-surfaces.test.ts` holds this block to the
  // registry: every Class A route resolves here, no B/C/D route does, and every
  // row here is claimed by a Class A view, an independent view, the Overview or
  // the installer.
  { prefix: 'sector-overlays', moduleId: 'sector_overlays' },
  { prefix: 'localization/sector-overlays', moduleId: 'sector_overlays' },
  { prefix: 'credit-information-supply', moduleId: 'sector_overlays' },
  { prefix: 'pcf-supervision', moduleId: 'sector_overlays' },
  { prefix: 'payments-evidence', moduleId: 'sector_overlays' },
  { prefix: 'investor-verifications', moduleId: 'sector_overlays' },
  { prefix: 'account-data-disclosures', moduleId: 'sector_overlays' },
  { prefix: 'manufacturing/batches', moduleId: 'sector_overlays' },
  { prefix: 'manufacturing/trace', moduleId: 'sector_overlays' },
  { prefix: 'manufacturing/epr', moduleId: 'sector_overlays' },
  { prefix: 'manufacturing/chemicals', moduleId: 'sector_overlays' },
  { prefix: 'manufacturing/chemical-traceability', moduleId: 'sector_overlays' },
  { prefix: 'food-traceability', moduleId: 'sector_overlays' },
  { prefix: 'regulated-accounts', moduleId: 'sector_overlays' },
  { prefix: 'logistics/customs', moduleId: 'sector_overlays' },
  { prefix: 'logistics/duty-free-lists', moduleId: 'sector_overlays' },
  { prefix: 'logistics/tax-examinations', moduleId: 'sector_overlays' },
  { prefix: 'logistics/tax-transition', moduleId: 'sector_overlays' },
  { prefix: 'logistics/claims', moduleId: 'sector_overlays' },
  { prefix: 'logistics/authorisations', moduleId: 'sector_overlays' },
  { prefix: 'public-data', moduleId: 'sector_overlays' },
  { prefix: 'biometric-capture', moduleId: 'sector_overlays' },
  { prefix: 'sector-licences/applications', moduleId: 'sector_overlays' },
  { prefix: 'sector-licences/fee-lines', moduleId: 'sector_overlays' },
  { prefix: 'marketing-communications/sender-name-certificates', moduleId: 'sector_overlays' },
  { prefix: 'platform-evidence', moduleId: 'sector_overlays' },
  { prefix: 'publications', moduleId: 'sector_overlays' },
  { prefix: 'publication-assessments', moduleId: 'sector_overlays' },
  { prefix: 'legal-representatives', moduleId: 'sector_overlays' },
  { prefix: 'student-records', moduleId: 'sector_overlays' },
  { prefix: 'cybersecurity-assurance-index', moduleId: 'sector_overlays' },

  { prefix: 'rights-requests', moduleId: 'rights_requests' },
  // Sprint 43.5.1 — `/admin/templates` route removed.
  { prefix: 'audit', moduleId: 'audit_trail' },
  { prefix: 'data-mapping', moduleId: 'data_mapping' },
  { prefix: 'governance', moduleId: 'governance' },
  { prefix: 'consent-properties', moduleId: 'consent_properties' },
  // Sprint 62.28.0 — the registers sit inside the consent module's tier set;
  // they are a sub-group, not a module (G-110). Declared before `consent` so
  // the longer prefix wins the match.
  { prefix: 'marketing-communications', moduleId: 'consent' },
  { prefix: 'consent', moduleId: 'consent' },
  { prefix: 'assessments', moduleId: 'assessments' },
  { prefix: 'classification', moduleId: 'classification' },
  { prefix: 'monitoring', moduleId: 'monitoring' },
  { prefix: 'incidents', moduleId: 'incidents' },
  { prefix: 'connectors', moduleId: 'connectors' },
  { prefix: 'vendor-risk', moduleId: 'vendor_risk' },
  { prefix: 'contracts', moduleId: 'contracts' },
  { prefix: 'deletion-orchestration', moduleId: 'deletion_orchestration' },
  { prefix: 'discovery', moduleId: 'discovery' },
  { prefix: 'incident-ops', moduleId: 'incident_ops' },
  { prefix: 'program-governance', moduleId: 'program_governance' },
  { prefix: 'access-accountability', moduleId: 'access_accountability' },
  { prefix: 'onboarding', moduleId: 'onboarding' },
  { prefix: 'localization', moduleId: 'localization' },
  { prefix: 'partner-marketplace', moduleId: 'partner_workspace' },
  { prefix: 'forms', moduleId: 'compliance_forms' },
  { prefix: 'tasks', moduleId: 'task_management' },
  { prefix: 'organizations', moduleId: 'organizations' },
  { prefix: 'org-groups', moduleId: 'organizations' },
  { prefix: 'intake-configs', moduleId: 'organizations' },
  { prefix: 'privacy-scanner', moduleId: 'privacy_scanner' },
  { prefix: 'data-subjects', moduleId: 'data_subjects' },
  // Sprint 62.7.0 — the three new surfaces are LEAVES of modules that already
  // exist, not modules of their own: Public Data Exchange sits inside Data
  // Mapping's sidebar section, Payments/eKYC Evidence and Platform Evidence
  // inside Governance's. There is deliberately no `public_data`,
  // `payments_evidence` or `platform_evidence` in `ModuleId` — minting one
  // would declare a new sellable module as a side effect of a sidebar edit
  // (G-110), and the tier counts stay at Light 15 / Standard 26 / Enterprise 29.
  //
  // They are mapped to their OWNING module so the route agrees with the
  // sidebar. `AdminLayout` hides the whole Data Mapping / Governance section
  // when its `moduleId` is disabled, taking these leaves with it; without these
  // rows the routes stayed reachable by URL in a tenant that had switched the
  // owning module off, so the nav said the module was gone and the router said
  // it was not.
  //
  // Sprint 63.43.0 — all three prefixes moved to Module 30 (Sector Overlays):
  // every view under them is overlay-exclusive (plan §2.1, Class A). Their rows
  // are in the Sector Overlays block at the top of this map; the reasoning above
  // (route follows the sidebar section's module) is unchanged.

  // Sprint 62.8.0. The split follows broadcast #10: an OT asset profile extends a
  // canonical system, which lives in Data Mapping (Light); everything else — and
  // remote access, because a grant names a vendor and the vendor register is
  // Standard — belongs to Program Governance. Sprint 63.43.0: batches, trace,
  // epr, chemicals and chemical-traceability are Class A and resolve to
  // `sector_overlays` through the rows at the top of this map; products,
  // conformity (Class B), capa-recall and remote-access stay here.
  { prefix: 'manufacturing/ot-assets', moduleId: 'data_mapping' },
  { prefix: 'manufacturing', moduleId: 'program_governance' },

  // Sprint 62.9.0. All seven leaves sit in **one** owning module, so one prefix
  // row rather than seven: unlike 62.8.0's OT split, no logistics leaf depends on
  // anything a Light tenant holds — a shipment's carrier and broker resolve in
  // `grc_vendors`, whose creating surfaces are all Standard — so there is nothing
  // here to split on. There is deliberately no `logistics` in `ModuleId`; minting
  // one would declare a 30th sellable module as a side effect of a sidebar edit
  // (G-110), and the tier counts stay at Light 15 / Standard 26 / Enterprise 29.
  // Sprint 63.43.0: customs, duty-free-lists, tax-examinations, tax-transition,
  // claims and authorisations are Class A and resolve to `sector_overlays`
  // through the rows at the top of this map; shipments, inspections, parcels,
  // delivery and the Class B tax-dossiers and tax-payments stay here.
  { prefix: 'logistics', moduleId: 'program_governance' },

  // Fix sprint 20260927-0100 F7 — the rows below close the disagreement
  // 62.7.0 closed for three prefixes, over every other leaf of a module-gated
  // sidebar section. Each leaf's own sidebar comment names its section as its
  // owning module (G-110: a sub-group is not a module); without a row here the
  // section vanished with its module and the page still rendered by URL.
  // `layouts/__tests__/sidebar-leaf-route-module-census-63.22.0.test.ts`
  // derives the population from `AdminLayout.tsx` and fails on any leaf of a
  // module-gated section whose route resolves to another module or to none,
  // so a new leaf cannot reopen this. Leaves deliberately left module-
  // independent are listed there, each with its reason.
  //
  // Assessments — Evidence Packs (62.2.5) and the AI System Register (62.3.0).
  { prefix: 'framework-evidence-packs', moduleId: 'assessments' },
  { prefix: 'ai-governance', moduleId: 'assessments' },
  // Assessments › Privacy Office and › Training — the Phase 28 DPO registers
  // (65.6.0 DT-01, DPO-01, PUB-01; 65.17.0 ANON-01; 65.18.0 IRP-01; 65.19.0
  // LEG-01; 65.21.0). Owner ruling 2026-09-27: they serve every tier, so they
  // moved out of Program Governance (Standard and above) into Assessments,
  // which every tier holds. Gating them on `assessments` keeps the section
  // and the route in agreement and changes no tier's reach; a tenant that
  // switches Assessments off loses them in the sidebar and the router alike.
  { prefix: 'privacy-office', moduleId: 'assessments' },
  { prefix: 'training', moduleId: 'assessments' },
  { prefix: 'deidentification', moduleId: 'assessments' },
  { prefix: 'continuity', moduleId: 'assessments' },
  { prefix: 'legal-matters', moduleId: 'assessments' },
  { prefix: 'credit-information', moduleId: 'assessments' },
  // Data Mapping — the regulatory classification subject register (62.20.0).
  { prefix: 'regulatory-classification', moduleId: 'data_mapping' },
  // Governance — the DPO work programme (65.1.0, persona P-VN-01 Enterprise
  // DPO), Trust & Identity Evidence (62.5.0, 62.39.0) and Regulatory
  // Artifacts (62.6.0): sub-groups beside Payments and Platform Evidence.
  { prefix: 'compliance-operations', moduleId: 'governance' },
  { prefix: 'trust-evidence', moduleId: 'governance' },
  { prefix: 'regulatory-artifacts', moduleId: 'governance' },
  // Regulatory › Regulatory Registers, › Penalties & Enforcement and › Data
  // Exploitation (under Localization until Sprint 63.42.2; still the localization
  // module) — the register leaves 62.21.0 moved into the old Localization ›
  // Sector Overlays group that stay in Localization (Light) after Sprint 63.43.0:
  // the shared (Class B) and baseline (Class C) views and legal holds. The
  // overlay-exclusive ones moved to the Sector Overlays block at the top of this
  // map. The census forces a row to move with its leaf.
  { prefix: 'penalty-exposure', moduleId: 'localization' },
  { prefix: 'enforcement-cases', moduleId: 'localization' },
  { prefix: 'authority-directions', moduleId: 'localization' },
  { prefix: 'legal-holds', moduleId: 'localization' },
  { prefix: 'regulatory-placeholders', moduleId: 'localization' },
  { prefix: 'sector-licences', moduleId: 'localization' },
  { prefix: 'data-service-providers', moduleId: 'localization' },
  { prefix: 'data-exploitation', moduleId: 'localization' },
  { prefix: 'controlled-testing', moduleId: 'localization' },
  { prefix: 'product-classifications', moduleId: 'localization' },
  { prefix: 'conformity-certificates', moduleId: 'localization' },
  { prefix: 'marketing-approvals', moduleId: 'localization' },
  { prefix: 'regulatory-disclosures', moduleId: 'localization' },
  // Organizations — case consolidation (the F7 finding).
  { prefix: 'consolidation', moduleId: 'organizations' },
  // Forseti — `SIDEBAR_MODULE_MAP.forseti`. Not a `ModuleId`, so
  // `isModuleEnabled` answers true and this row changes nothing today; it
  // makes the route follow the sidebar the day the key becomes configurable,
  // rather than leaving the section and its pages to disagree then.
  { prefix: 'forseti', moduleId: 'forseti' },
];

/**
 * Look up the ModuleId for a given route path (relative to /admin/).
 * Returns undefined if the route has no module gating.
 */
export function getRouteModule(relativePath: string): string | undefined {
  for (const entry of ROUTE_MODULE_MAP) {
    if (relativePath === entry.prefix || relativePath.startsWith(entry.prefix + '/')) {
      return entry.moduleId;
    }
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// Sidebar section → ModuleId (for sidebar visibility gating)
// ---------------------------------------------------------------------------

/**
 * Maps sidebar section keys to ModuleId values. Sections not listed here are
 * always visible regardless of module configuration (dashboard, platform, help).
 */
export const SIDEBAR_MODULE_MAP: Record<string, string> = {
  rights_requests: 'rights_requests',
  data_mapping: 'data_mapping',
  governance: 'governance',
  consent: 'consent',
  consent_properties: 'consent_properties',
  assessments: 'assessments',
  classification: 'classification',
  monitoring: 'monitoring',
  incidents: 'incidents',
  connectors: 'connectors',
  vendor_risk: 'vendor_risk',
  deletion_orchestration: 'deletion_orchestration',
  discovery: 'discovery',
  incident_ops: 'incident_ops',
  program_governance: 'program_governance',
  access_accountability: 'access_accountability',
  onboarding: 'onboarding',
  localization: 'localization',
  partner_workspace: 'partner_workspace',
  compliance_forms: 'compliance_forms',
  privacy_scanner: 'privacy_scanner',
  audit_trail: 'audit_trail',
  task_management: 'task_management',
  organizations: 'organizations',
  data_subjects: 'data_subjects',
  // Sprint 47.5.2 — Forseti AI Legal Advisor.
  forseti: 'forseti',
  // Sprint 63.43.0 — Module 30, Sector Overlays (`ModuleId.SECTOR_OVERLAYS`,
  // Enterprise only, owner ruling OR-26).
  sector_overlays: 'sector_overlays',
};
